import React, { useState, useEffect, useRef } from "react";

export default function CandidateScan() {
  const useAIGrading = false; // Flag: false = simple keyword matching, true = AI/NLP grading

  const candidates = [
    { id: 1, name: "Rahul Sharma", phone: "+91 98XXXX432", skills: ["React", "Node", "MongoDB"], experience: "3.5 Years", education: "B.Tech – CSE" },
    { id: 2, name: "Ayesha Khan", phone: "+91 97XXXX112", skills: ["Python", "Django", "PostgreSQL"], experience: "4 Years", education: "MCA" },
    { id: 3, name: "Sanjeev Kumar", phone: "+91 98XXXX432", skills: ["React", "Node", "MongoDB"], experience: "3 Years", education: "B.Tech – Electronics" },
    { id: 4, name: "Babita Sinha", phone: "+91 97XXXX112", skills: ["Macro Economics", "Economic Survey"], experience: "6 Years", education: "Masters in Economics" },
  ];

  const educationQuestions = {
    "B.Tech – CSE": [
      "Explain your recent software project.",
      "How do you debug a complex React issue?",
      "Describe a Node.js challenge you solved.",
      "How do you optimize MongoDB queries?",
      "Which modern web technology are you most comfortable with?"
    ],
    "MCA": [
      "Describe your most complex Python project.",
      "How do you optimize Django apps for performance?",
      "Explain your experience with databases like PostgreSQL.",
      "What algorithms do you often use in programming?",
      "How do you approach debugging and testing?"
    ],
    "B.Tech – Electronics": [
      "Explain a circuit design you worked on.",
      "How do you troubleshoot embedded systems?",
      "Describe your experience with microcontrollers.",
      "How do you optimize electronic system performance?",
      "Which tools do you use for simulation/testing?"
    ],
    "Masters in Economics": [
      "Explain a recent economic analysis you did.",
      "How do you interpret macroeconomic indicators?",
      "Describe your experience with statistical modeling.",
      "Which economic research methods do you use?",
      "How would you advise a government policy using data?"
    ]
  };

  const expectedAnswers = {
    "Explain your recent software project.": ["react", "node", "mongodb", "project", "web"],
    "How do you debug a complex React issue?": ["debug", "react", "console", "error", "component"],
    "Describe a Node.js challenge you solved.": ["node", "server", "api", "async", "callback"],
    "How do you optimize MongoDB queries?": ["mongodb", "index", "query", "performance"],
    "Which modern web technology are you most comfortable with?": ["react", "vue", "angular", "javascript"],
    // Add more expected keywords for other questions
  };

  const getQuestionsForCandidate = (candidate) => {
    return educationQuestions[candidate.education] || [
      "Tell me about your recent project.",
      "How do you handle production bugs?",
      "Explain one technical challenge you solved.",
      "What motivates you in your field?",
      "How do you continue learning in your domain?"
    ];
  };

  const [activeCandidate, setActiveCandidate] = useState(0);
  const [stage, setStage] = useState("resume");
  const [transcript, setTranscript] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [recordingQuestion, setRecordingQuestion] = useState(null);
  const [speechAnswer, setSpeechAnswer] = useState("");
  const [candidateCameraActive, setCandidateCameraActive] = useState(false);
  const [aiCameraActive, setAiCameraActive] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [interviewStartTime, setInterviewStartTime] = useState(null);
  const [interviewEndTime, setInterviewEndTime] = useState(null);
  const [questionTimes, setQuestionTimes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [dynamicScore, setDynamicScore] = useState(0);
  const [dynamicGrade, setDynamicGrade] = useState("C");

  const candidateVideoRef = useRef(null);
  const aiVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  const c = candidates[activeCandidate];
  const currentQuestions = getQuestionsForCandidate(c);

  const nextStage = () => {
    if (stage === "resume") {
      setStage("calling");
    } else if (stage === "calling") {
      setStage("questions");
      setInterviewStartTime(new Date());
      setQuestionTimes([]);
      startVideoRecording();
    } else if (stage === "questions") {
      setStage("saving");
      setInterviewEndTime(new Date());
      stopVideoRecording();
      generateSummary();
    } else if (stage === "saving") {
      // Reset for next candidate
      setTranscript([]);
      setSpeechAnswer("");
      setRecordingQuestion(null);
      setCandidateCameraActive(false);
      setAiCameraActive(false);
      setCurrentQuestionIndex(0);
      setInterviewStartTime(null);
      setInterviewEndTime(null);
      setQuestionTimes([]);
      setSummary(null);
      setVideoBlob(null);
      setDynamicScore(0);
      setDynamicGrade("C");
      setStage("resume");
      setActiveCandidate((prev) => (prev + 1) % candidates.length);
    }
  };

  // Camera management
  useEffect(() => {
    if (stage === "questions") {
      // Candidate camera
      setCandidateCameraActive(true);
      navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        .then((stream) => {
          if (candidateVideoRef.current) {
            candidateVideoRef.current.srcObject = stream;
            candidateVideoRef.current.play();
          }
        })
        .catch((err) => {
          console.error("Candidate camera error:", err);
          setCandidateCameraActive(false);
        });

      // AI camera
      setAiCameraActive(true);
      navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        .then((stream) => {
          if (aiVideoRef.current) {
            aiVideoRef.current.srcObject = stream;
            aiVideoRef.current.play();
          }
        })
        .catch((err) => {
          console.error("AI camera error:", err);
          setAiCameraActive(false);
        });
    } else {
      if (candidateVideoRef.current?.srcObject) {
        candidateVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
        candidateVideoRef.current.srcObject = null;
      }
      if (aiVideoRef.current?.srcObject) {
        aiVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
        aiVideoRef.current.srcObject = null;
      }
      setCandidateCameraActive(false);
      setAiCameraActive(false);
    }
  }, [stage]);

  const startVideoRecording = () => {
    if (candidateVideoRef.current?.srcObject) {
      const stream = candidateVideoRef.current.srcObject;
      const mediaRecorder = new MediaRecorder(stream);
      recordedChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
        setVideoBlob(blob);
        // console.log("Video recorded and ready to save", blob);
      };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
    }
  };

  const stopVideoRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const startRecording = (q) => {
    if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
      alert("Speech Recognition not supported in this browser.");
      return;
    }

    setRecordingQuestion(q);
    setSpeechAnswer("");

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    setQuestionTimes(prev => [...prev, { q, startTime: new Date(), endTime: null }]);

    recognition.start();

    recognition.onresult = (event) => {
      const transcriptText = event.results[0][0].transcript;
      setSpeechAnswer(transcriptText);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setSpeechAnswer("Error capturing voice. Try again.");
    };

    recognition.onend = () => {
      // console.log("Speech recognition ended.");
    };
  };

  const saveSpeechAnswer = async () => {
    if (!speechAnswer || !recordingQuestion) return;

    setQuestionTimes(prev => {
      const last = prev.find(qt => qt.q === recordingQuestion && !qt.endTime);
      if (last) last.endTime = new Date();
      return [...prev];
    });

    // Evaluate answer dynamically
    let marks = 0;
    let grade = "C";

    if (useAIGrading) {
      // Here you can integrate NLP/AI API like ChatGPT for answer scoring
      // For now, simulate with random marks
      marks = Math.floor(Math.random() * 6);
    } else {
      // Simple keyword matching
      const expected = expectedAnswers[recordingQuestion] || [];
      const answerWords = speechAnswer.toLowerCase().split(/\s+/);
      const matched = expected.filter(word => answerWords.includes(word));
      marks = Math.min(matched.length, 5);
    }

    if (marks >= 4) grade = "A";
    else if (marks >= 3) grade = "B";

    // Update dynamic score
    setDynamicScore(prev => prev + marks);
    const avg = (dynamicScore + marks) / (currentQuestionIndex + 1);
    if (avg >= 4) setDynamicGrade("A");
    else if (avg >= 3) setDynamicGrade("B");
    else setDynamicGrade("C");

    setTranscript(prev => [...prev, { q: recordingQuestion, a: speechAnswer, marks, grade }]);
    setRecordingQuestion(null);
    setSpeechAnswer("");

    // Move to next question or finish
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      nextStage();
    }

    simulateAIVoiceAnswer();
  };

  const simulateAIVoiceAnswer = () => {
    setAiSpeaking(true);
    const utterance = new SpeechSynthesisUtterance(`AI response for testing purposes.`);
    utterance.lang = "en-US";
    utterance.onend = () => setAiSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const generateSummary = () => {
    const totalMarks = transcript.reduce((sum, q) => sum + (q.marks || 0), 0);
    const averageMarks = totalMarks / transcript.length;
    let overallGrade = "C";
    if (averageMarks >= 4) overallGrade = "A";
    else if (averageMarks >= 3) overallGrade = "B";

    setSummary({
      transcript,
      totalMarks,
      averageMarks,
      overallGrade,
      interviewStartTime,
      interviewEndTime,
      questionTimes,
      videoBlob
    });

    // Export JSON
    const exportData = {
      candidate: c,
      transcript,
      totalMarks,
      averageMarks,
      overallGrade,
      interviewStartTime,
      interviewEndTime,
      questionTimes
    };
    // console.log("Export JSON:", exportData);
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 space-y-4">
      <h1 className="text-xl font-bold text-center">🤖 Think5Whys CandidateScan – AI Interview Engine</h1>
      <p className="text-gray-300">
        CandidateScan – AI Interview Engine is a web-based intelligent interview platform designed to transform traditional hiring by combining live candidate interactions with AI-assisted evaluation. It streamlines the interview workflow by allowing recruiters to upload resumes, initiate AI-simulated calls, conduct live voice-based interviews, and record both candidate and AI video streams simultaneously. Candidate answers are captured in real-time using speech recognition, timestamped per question, and stored along with an automatic scoring and grading system. The platform supports flexible grading modes: simple keyword matching for expected answers or advanced NLP/AI evaluation via integration with services like ChatGPT. It provides dynamic progress tracking, per-question timing, cumulative scores, and overall grades, alongside the ability to export transcripts and video recordings for future reference. CandidateScan is ideal for tech and non-tech roles alike, accommodating structured question sets based on education or domain expertise, ensuring fairness, consistency, and efficiency in recruitment processes.
      </p>
      {/* Resume Bank */}
      <div className="border border-gray-700 rounded-xl p-4">
        <h2 className="font-semibold mb-2 text-yellow-200">📁 Resume Bank</h2>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {candidates.map((candidate, i) => (
            <div key={candidate.id} className={`text-white p-2 rounded border ${i === activeCandidate ? "border-green-500" : "border-gray-700"}`}>
              {candidate.name}
            </div>
          ))}
        </div>
      </div>

      {/* Main Flow */}
      <div className="border border-gray-700 rounded-xl p-4 space-y-3">
        {stage === "resume" && (
          <>
            <h2 className="font-semibold text-yellow-200">📄 Reading Resume</h2>
            <div className="text-sm space-y-1 [&_p]:text-white">
              <p><b>Name:</b> {c.name}</p>
              <p><b>Phone:</b> {c.phone}</p>
              <p><b>Experience:</b> {c.experience}</p>
              <p><b>Education:</b> {c.education}</p>
              <p><b>Skills:</b> {c.skills.join(", ")}</p>
            </div>
          </>
        )}

        {stage === "calling" && (
          <>
            <h2 className="font-semibold text-yellow-200">📞 AI Calling Agent</h2>
            <p className="text-sm text-green-400">Calling {c.name}...</p>
            <p className="text-xs text-gray-400">“Hello, I’m the AI Hiring Assistant. This call will take 5 minutes.”</p>
          </>
        )}

        {stage === "questions" && (
          <>
            <h2 className="font-semibold text-yellow-200">🎙️ Interview in Progress</h2>
            {candidateCameraActive && (
              <div className="relative border border-gray-700 rounded mb-2">
                <video ref={candidateVideoRef} className="w-full rounded" autoPlay muted />
                {aiCameraActive && (
                  <video ref={aiVideoRef} className="absolute top-2 right-2 w-24 h-24 border-2 border-yellow-400 rounded" autoPlay muted />
                )}
              </div>
            )}

            {/* Progress + Score */}
            <div className="flex justify-between mb-2 items-center">
              <div className="w-3/4 bg-gray-700 rounded h-2">
                <div className="bg-green-500 h-2 rounded" style={{ width: `${(currentQuestionIndex / currentQuestions.length) * 100}%` }} />
              </div>
              <div className="text-xs ml-2">
                Score: {dynamicScore} | Grade: {dynamicGrade}
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-2">
              {currentQuestions.map((q, i) => i === currentQuestionIndex && (
                <div key={i} className="border border-gray-700 rounded p-2 text-sm">
                  <p className="text-blue-400">Q: {q}</p>
                  {!recordingQuestion || recordingQuestion !== q ? (
                    <button onClick={() => startRecording(q)} className="w-40 mt-1 text-xs bg-white text-black px-2 py-2 rounded">🎤 Click to Speak</button>
                  ) : (
                    <div className="flex flex-col space-y-1">
                      <textarea className="w-full p-1 rounded bg-gray-800 text-white text-sm" value={speechAnswer} readOnly placeholder="Recording..." />
                      <button onClick={saveSpeechAnswer} className="w-32 mt-1 text-xs bg-green-500 text-black px-2 py-2 rounded">💾 Save Answer</button>
                    </div>
                  )}
                  {aiSpeaking && <p className="text-yellow-400 text-xs mt-1">AI is speaking...</p>}
                </div>
              ))}
            </div>
          </>
        )}

        {stage === "saving" && summary && (
          <>
            <h2 className="font-semibold text-yellow-200">🧠 Interview Summary</h2>
            <div className="text-xs space-y-1 [&_p]:text-white">
              <p><b>Interview Start:</b> {summary.interviewStartTime.toLocaleTimeString()}</p>
              <p><b>Interview End:</b> {summary.interviewEndTime.toLocaleTimeString()}</p>
              {summary.transcript.map((t, i) => (
                <p key={i}>
                  <b>Q:</b> {t.q}<br/>
                  <b>A:</b> {t.a}<br/>
                  <b>Marks:</b> {t.marks}<br/>
                  <b>Grade:</b> {t.grade}<br/>
                  <b>Time Taken:</b> {summary.questionTimes[i]?.startTime && summary.questionTimes[i]?.endTime
                    ? `${((summary.questionTimes[i].endTime - summary.questionTimes[i].startTime)/1000).toFixed(1)} sec`
                    : "N/A"}
                </p>
              ))}
              <p className="mt-2"><b>Total Marks:</b> {summary.totalMarks}</p>
              <p><b>Average Marks:</b> {summary.averageMarks.toFixed(2)}</p>
              <p><b>Overall Grade:</b> {summary.overallGrade}</p>
              {videoBlob && (
                <>
                  <p className="mt-2">Recorded Interview:</p>
                  <video src={URL.createObjectURL(videoBlob)} controls className="w-full rounded" />
                </>
              )}
            </div>
          </>
        )}
      </div>

      <button onClick={nextStage} className="w-40 bg-white text-black py-2 rounded-md font-semibold">▶ Next Step</button>
    </div>
  );
}
